import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Play,
  QrCode,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { newId } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import { OfflineBanner } from '../../components/SyncIndicator';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  ListRow,
  ProgressBar,
  Spinner,
  Stat,
  StatusPill,
  complianceTone,
  severityTone,
} from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { currentShift, deviceInfo, formatTime, humanise, relativeTime } from '../../lib/format';

interface DueItem {
  scheduleId: string;
  templateId: string;
  templateName: string;
  templateCode: string;
  machineId: string;
  machineCode: string;
  machineName: string;
  lineName: string;
  dueAt: string;
  dueBy: string;
  status: string;
  runId: string | null;
  label: string;
}

interface OperatorDashboard {
  operator: { id: string; name: string; shift: string | null };
  due: DueItem[];
  inProgress: Array<{
    id: string;
    status: string;
    totalItems: number;
    completedItems: number;
    abnormalCount: number;
    rejectionReason: string | null;
    templateName: string;
    machineCode: string;
    machineName: string;
  }>;
  completedToday: number;
  openProblems: Array<{
    id: string;
    tagNo: string;
    title: string;
    severity: string;
    status: string;
    machineCode: string;
    detectedAt: string;
  }>;
  training: Array<{ id: string; oplId: string; oplNo: string; title: string; status: string }>;
}

/**
 * The operator's answer to "what do I need to check now?" (directive §79).
 * Due work first, in-progress second, everything else below.
 */
export function OperatorHome() {
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'operator'],
    queryFn: () => api.get<OperatorDashboard>('/api/dashboard/operator'),
  });

  const startRun = useMutation({
    mutationFn: async (due: DueItem) => {
      const runId = newId();
      return api.post<{ run: { id: string } }>('/api/checklist-runs', {
        id: runId,
        templateId: due.templateId,
        machineId: due.machineId,
        scheduleId: due.scheduleId,
        shift: user?.shift ?? currentShift(),
        dueDate: due.dueAt,
        deviceInfo: deviceInfo(),
        clientCreatedAt: new Date().toISOString(),
      });
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate(`/checklists/${result.run.id}`);
    },
  });

  if (isLoading) return <Spinner label="Loading your work" />;

  if (error || !data) {
    return (
      <div className="p-4">
        <EmptyState
          icon={AlertTriangle}
          title="Could not load your dashboard"
          message="Check your connection. Any checklist already open on this device still works offline."
          action={
            <Button onClick={() => void queryClient.invalidateQueries({ queryKey: ['dashboard'] })}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  const firstName = data.operator.name.split(' ')[0] ?? data.operator.name;
  const overdue = data.due.filter((d) => d.status === 'OVERDUE');

  return (
    <div>
      <OfflineBanner />
      <PageHeader
        title={`${greeting()}, ${firstName}`}
        subtitle={
          data.operator.shift
            ? `Shift ${data.operator.shift} · ${new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}`
            : new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
        }
        actions={
          <Button icon={QrCode} onClick={() => navigate('/scan')}>
            Scan machine
          </Button>
        }
      />

      <div className="space-y-4 p-4 lg:p-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat
            label="Due now"
            value={data.due.length}
            tone={overdue.length > 0 ? 'warn' : 'neutral'}
            hint={overdue.length > 0 ? `${overdue.length} overdue` : 'On schedule'}
            icon={ClipboardList}
          />
          <Stat
            label="In progress"
            value={data.inProgress.length}
            tone="info"
            icon={Play}
          />
          <Stat
            label="Done today"
            value={data.completedToday}
            tone="ok"
            icon={CheckCircle2}
          />
          <Stat
            label="My open problems"
            value={data.openProblems.length}
            tone={data.openProblems.length > 0 ? 'warn' : 'neutral'}
            icon={AlertTriangle}
          />
        </div>

        {/* In progress first — finishing beats starting. */}
        {data.inProgress.length > 0 ? (
          <Card>
            <CardHeader title="Carry on where you left off" />
            <ul className="divide-y divide-steel-100">
              {data.inProgress.map((run) => (
                <li key={run.id}>
                  <ListRow onClick={() => navigate(`/checklists/${run.id}`)}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-steel-900">
                          {run.machineCode} · {run.templateName}
                        </p>
                        <p className="truncate text-xs text-steel-500">{run.machineName}</p>
                      </div>
                      {run.abnormalCount > 0 ? (
                        <StatusPill tone="warn" label={`${run.abnormalCount} abnormal`} />
                      ) : null}
                    </div>
                    {run.rejectionReason ? (
                      <p className="mt-1 rounded bg-alarm-50 px-2 py-1 text-xs font-semibold text-alarm-700">
                        Returned by supervisor: {run.rejectionReason}
                      </p>
                    ) : null}
                    <div className="mt-2">
                      <ProgressBar
                        value={run.completedItems}
                        total={run.totalItems}
                        tone="info"
                        label="Tasks"
                      />
                    </div>
                  </ListRow>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        <Card>
          <CardHeader
            title="Checks due"
            subtitle="Scheduled CILT work on your line"
            action={
              <Link to="/checklists" className="text-xs font-bold text-info-700 hover:underline">
                All my checklists
              </Link>
            }
          />
          {data.due.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="Nothing due right now"
              message="You are up to date on scheduled CILT checks. Scan a machine if you need to start something else."
              action={
                <Button variant="secondary" icon={QrCode} onClick={() => navigate('/scan')}>
                  Scan a machine
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-steel-100">
              {data.due.map((due) => (
                <li key={`${due.scheduleId}-${due.dueAt}`} className="px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-steel-900">{due.machineCode}</p>
                        <StatusPill
                          tone={complianceTone(due.status)}
                          label={humanise(due.status)}
                        />
                      </div>
                      <p className="text-sm text-steel-700">{due.templateName}</p>
                      <p className="text-xs text-steel-500">
                        {due.machineName} · {due.lineName} · due {formatTime(due.dueAt)}
                        {due.status === 'OVERDUE' ? ` (${relativeTime(due.dueAt)})` : ''}
                      </p>
                    </div>
                    <Button
                      size="lg"
                      icon={Play}
                      loading={startRun.isPending && startRun.variables?.scheduleId === due.scheduleId}
                      onClick={() =>
                        due.runId
                          ? navigate(`/checklists/${due.runId}`)
                          : startRun.mutate(due)
                      }
                    >
                      {due.runId ? 'Continue' : 'Start'}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {startRun.isError ? (
          <p role="alert" className="text-sm font-semibold text-alarm-600">
            Could not start that checklist. {(startRun.error as Error).message}
          </p>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader
              title="Problems I raised"
              action={
                <Link to="/problems?mine=1" className="text-xs font-bold text-info-700 hover:underline">
                  View all
                </Link>
              }
            />
            {data.openProblems.length === 0 ? (
              <EmptyState
                icon={CheckCircle2}
                title="No open problems"
                message="Nothing you raised is still waiting to be fixed."
              />
            ) : (
              <ul className="divide-y divide-steel-100">
                {data.openProblems.map((problem) => (
                  <li key={problem.id}>
                    <ListRow onClick={() => navigate(`/problems/${problem.id}`)}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-steel-900">
                            {problem.tagNo} · {problem.title}
                          </p>
                          <p className="text-xs text-steel-500">
                            {problem.machineCode} · {humanise(problem.status)} ·{' '}
                            {relativeTime(problem.detectedAt)}
                          </p>
                        </div>
                        <StatusPill
                          tone={severityTone(problem.severity)}
                          label={humanise(problem.severity)}
                        />
                      </div>
                    </ListRow>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Training waiting for you"
              action={
                <Link to="/training" className="text-xs font-bold text-info-700 hover:underline">
                  My record
                </Link>
              }
            />
            {data.training.length === 0 ? (
              <EmptyState
                icon={ClipboardCheck}
                title="No training outstanding"
                message="You are up to date on your One-Point Lessons."
              />
            ) : (
              <ul className="divide-y divide-steel-100">
                {data.training.map((training) => (
                  <li key={training.id}>
                    <ListRow onClick={() => navigate(`/opl/${training.oplId}`)}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-steel-900">
                            <BookOpen aria-hidden className="mr-1 inline size-3.5" />
                            {training.oplNo} · {training.title}
                          </p>
                        </div>
                        <StatusPill
                          tone={training.status === 'NEEDS_RETRAINING' ? 'warn' : 'info'}
                          label={humanise(training.status)}
                        />
                      </div>
                    </ListRow>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}
